import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

const GA_TRACKING_ID = import.meta.env.VITE_GA_TRACKING_ID


export const initGA = () => {
  if (typeof window !== 'undefined' && GA_TRACKING_ID) {
   
    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`
    document.head.appendChild(script)

    // Initialize gtag
    window.dataLayer = window.dataLayer || []
    function gtag() {
      window.dataLayer.push(arguments)
    }
    window.gtag = gtag
    gtag('js', new Date())
    gtag('config', GA_TRACKING_ID, {
      page_title: document.title,
      page_location: window.location.href,
    })
  }
}

// Track page views
export const trackPageView = (url) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GA_TRACKING_ID, {
      page_path: url,
    })
  }
}

// Track custom events
export const trackEvent = (action, category, label, value) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    })
  }
}

// Track conversions
export const trackConversion = (conversionId, conversionLabel) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'conversion', {
      send_to: `${conversionId}/${conversionLabel}`,
    })
  }
}

// Track user login with role
export const trackLogin = (userRole, loginMethod = 'email') => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'login', {
      method: loginMethod,
      user_role: userRole,
      event_category: 'authentication',
      event_label: `${userRole}_login`
    })
  }
}

// Track dashboard visits by role
export const trackDashboardVisit = (userRole, dashboardType = 'main') => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'dashboard_visit', {
      user_role: userRole,
      dashboard_type: dashboardType,
      event_category: 'navigation',
      event_label: `${userRole}_dashboard`
    })
  }
}

// Track specific feature usage
export const trackFeatureUsage = (featureName, userRole, action = 'view') => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'feature_usage', {
      feature_name: featureName,
      user_role: userRole,
      action: action,
      event_category: 'engagement',
      event_label: `${featureName}_${action}`
    })
  }
}

// Track button clicks
export const trackButtonClick = (buttonName, userRole = 'guest', location = 'unknown') => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'button_click', {
      button_name: buttonName,
      user_role: userRole,
      location: location,
      event_category: 'interaction',
      event_label: `${buttonName}_click`
    })
  }
}

// Track form submissions
export const trackFormSubmission = (formName, userRole = 'guest', success = true) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'form_submission', {
      form_name: formName,
      user_role: userRole,
      success: success,
      event_category: 'engagement',
      event_label: `${formName}_${success ? 'success' : 'failure'}`
    })
  }
}

// Track page engagement time
export const trackEngagement = (pageName, timeSpent, userRole = 'guest') => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'engagement_time', {
      page_name: pageName,
      time_spent: timeSpent,
      user_role: userRole,
      event_category: 'engagement',
      event_label: `${pageName}_${timeSpent}s`
    })
  }
}

// Hook for automatic page view tracking
export const usePageTracking = () => {
  const location = useLocation()

  useEffect(() => {
    if (GA_TRACKING_ID) {
      trackPageView(location.pathname + location.search)
    }
  }, [location])
}

// Google Analytics Component
const GoogleAnalytics = () => {
  useEffect(() => {
    initGA()
  }, [])

  usePageTracking()

  return null
}

export default GoogleAnalytics
